import Phaser from 'phaser';

const PARTICLE_COUNT = 12;
const BURST_SPEED = 80;
const BURST_DURATION_MS = 400;
const PARTICLE_RADIUS = 2;

interface BurstParticle {
  readonly graphics: Phaser.GameObjects.Arc;
  readonly velocityX: number;
  readonly velocityY: number;
  elapsed: number;
}

interface ActiveBurst {
  readonly particles: BurstParticle[];
}

/**
 * Creates a brief, satisfying particle burst effect when a ghost is eaten.
 * Particles spread outward from the eat position and fade away quickly.
 */
export class GhostEatBurstManager {
  private readonly scene: Phaser.Scene;
  private readonly bursts: ActiveBurst[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  spawn(x: number, y: number, ghostColor: number): void {
    const particles: BurstParticle[] = [];
    const colors = [ghostColor, 0xffffff, 0xffff00, ghostColor];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + Math.random() * 0.3;
      const speed = BURST_SPEED * (0.6 + Math.random() * 0.8);
      const color = colors[i % colors.length] ?? 0xffffff;

      const circle = this.scene.add.circle(x, y, PARTICLE_RADIUS, color, 1)
        .setDepth(140);

      particles.push({
        graphics: circle,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed,
        elapsed: 0,
      });
    }

    this.bursts.push({ particles });
  }

  update(delta: number): void {
    const deltaSeconds = delta / 1000;

    for (let bi = this.bursts.length - 1; bi >= 0; bi--) {
      const burst = this.bursts[bi];
      if (!burst) continue;

      let allDone = true;

      for (let pi = burst.particles.length - 1; pi >= 0; pi--) {
        const particle = burst.particles[pi];
        if (!particle) continue;

        particle.elapsed += delta;
        const progress = particle.elapsed / BURST_DURATION_MS;

        if (progress >= 1) {
          particle.graphics.destroy();
          burst.particles.splice(pi, 1);
          continue;
        }

        allDone = false;

        // Move outward with deceleration
        const decel = 1 - progress * 0.7;
        particle.graphics.x += particle.velocityX * deltaSeconds * decel;
        particle.graphics.y += particle.velocityY * deltaSeconds * decel;

        // Shrink and fade
        const scale = 1 - progress * 0.8;
        particle.graphics.setScale(scale);
        particle.graphics.setAlpha(1 - progress);
      }

      if (allDone || burst.particles.length === 0) {
        this.bursts.splice(bi, 1);
      }
    }
  }

  destroy(): void {
    for (const burst of this.bursts) {
      for (const particle of burst.particles) {
        particle.graphics.destroy();
      }
    }
    this.bursts.length = 0;
  }
}
